import React, { useEffect, useState, useCallback, useRef } from 'react';
import './ReportGen.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFlask, faDna, faBaby, faTooth, faDroplet, faEllipsisV, faLayerGroup,
  faChevronCircleRight
} from '@fortawesome/free-solid-svg-icons';
// import KoreanFlag from '../flags/1f1f0-1f1f7.svg'; // Assuming these are used elsewhere or in RecentReports
// import TurkishFlag from '../flags/1f1f9-1f1f7.svg';
// import AmericanFlag from '../flags/1f1fa-1f1f8.svg';
import TempInputRow from './TempInputRow';
import RecentReports from './RecentReports'; // Make sure this component is robust
import StorageInfo from './StorageInfo/StorageInfo';

const INITIAL_LOAD_LIMIT = 50; // Initial items
const PAGINATION_LIMIT = 25;    // Items per scroll-triggered load
const RECENT_REFRESH_LIMIT = 50;// Top items to refresh
const REFRESH_INTERVAL = 3000;  // 3 seconds

function ReportGen({ user }) {
  const [items, setItems] = useState([]);
  const [inputRows, setInputRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState(new Set());
  const [flashInvalidRows, setFlashInvalidRows] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  const observerTarget = useRef(null); // For IntersectionObserver

  const fetchData = useCallback(async (page, limit, isInitialLoad = false) => {
    if (isLoadingMore && !isInitialLoad) return; // Prevent multiple concurrent loads for "load more"
    
    if (isInitialLoad) setIsLoadingInitial(true);
    else setIsLoadingMore(true);

    try {
      const response = await fetch(`https://service3.szapfs.org/api/report_requests?page=${page}&limit=${limit}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      
      setItems(prevItems => (page === 1 ? result.data : [...prevItems, ...result.data]));
      setCurrentPage(result.pagination.currentPage);
      setHasNextPage(result.pagination.hasNextPage);
      if (isInitialLoad) setInitialDataLoaded(true);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      if (isInitialLoad) setIsLoadingInitial(false);
      else setIsLoadingMore(false);
    }
  }, [isLoadingMore]); // Add isLoadingMore to prevent re-triggering fetchData if already loading more

  // Initial data load
  useEffect(() => {
    fetchData(1, INITIAL_LOAD_LIMIT, true);
  }, []); // Removed fetchData from deps, as it's stable due to useCallback with specific deps

  // Periodic refresh of the top N items (using the potentially safer merge logic)
  useEffect(() => {
    if (!initialDataLoaded) return;

    const refreshRecentItems = async () => {
      try {
        const response = await fetch(`https://service3.szapfs.org/api/report_requests/recent?limit=${RECENT_REFRESH_LIMIT}`);
        if (!response.ok) {
          console.error(`Error fetching recent items: ${response.status}`);
          return;
        }
        const recentData = await response.json();

        setItems(prevItems => {
          const recentItemIDs = new Set(recentData.map(item => item.ReportID));
          const olderItemsToKeep = prevItems.filter(item => !recentItemIDs.has(item.ReportID));
          const combinedItems = [...recentData, ...olderItemsToKeep];
          return combinedItems.sort((a, b) => Number(b.ReportID) - Number(a.ReportID));
        });
      } catch (error) {
        console.error('Error in refreshRecentItems interval:', error);
      }
    };

    const intervalId = setInterval(refreshRecentItems, REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [initialDataLoaded]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isLoadingMore && !isLoadingInitial) {
          fetchData(currentPage + 1, PAGINATION_LIMIT);
        }
      },
      { threshold: 1.0 } // Trigger when 100% of the target is visible
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasNextPage, isLoadingMore, isLoadingInitial, currentPage, fetchData]); // Include all relevant deps

  // --- Your existing functions for inputRows, validation, queue ---
  const addNewInputRow = () => {
    const newRow = {
      id: Date.now(), PatientFirstName: '', PatientLastName: '', ReportType: '',
      PatientDateOfBirth: '', PatientGender: '', Preset: '', Details: '',
      Company: '', Language: '', Newborn: {},
    };
    setInputRows([...inputRows, newRow]);
  };

  const setNewReport = (id, updatedReport) => {
    setInputRows(prevInputRows =>
      prevInputRows.map(row => (row.id === id ? { ...row, ...updatedReport } : row))
    );
  };

  const validateReport = (report) => {
    return (
      report.Preset && report.isValidId && report.ReportType &&
      report.Company && report.Language
    );
  };

  const handleRunQueue = () => {
    const newInvalidRows = new Set();
    const validReports = [];
    inputRows.forEach((report) => {
      if (!validateReport(report)) newInvalidRows.add(report.id);
      else validReports.push(report);
    });
    setInvalidRows(newInvalidRows);
    if (newInvalidRows.size > 0) {
      setFlashInvalidRows(true);
      setTimeout(() => setFlashInvalidRows(false), 600);
    }

    const languageMap = { Russian: 'RUS', Japanese: 'JPN', Korean: 'KOR', English: 'ENG', Turkish: 'TUR', Arabic: 'ARB', Spanish: 'SPA', Chinese: 'CHN' };
    const postReports = validReports.map(report => {
      const truncatedLanguage = languageMap[report.Language] || report.Language;
      const processedReport = {
        ...report,
        ReportType: `${report.ReportType} (${truncatedLanguage})`,
        Details: report.Newborn || {},
        Company: report.Company, Language: report.Language,
      };
      delete processedReport.isValidId; delete processedReport.Newborn;
      return fetch('https://service3.szapfs.org/api/reportinputreq', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(processedReport),
      });
    });
  
    Promise.all(postReports)
      .then(responses => Promise.all(responses.map(res => res.json())))
      .then(results => {
        console.log("Queue responses received:", results);
        setInputRows(inputRows.filter(row => !validateReport(row)));
        // After successful queue run, new items are added to the DB.
        // The periodic refresh will pick them up. Or you could trigger an immediate refresh:
        // refreshRecentItems(); // (if refreshRecentItems is exposed or refetched)
      })
      .catch(error => console.error('Error running queue:', error));
  };

  const handleDeleteReport = (id) => {
    setInputRows(inputRows.filter(row => row.id !== id));
    const newInvalidRows = new Set(invalidRows);
    newInvalidRows.delete(id);
    setInvalidRows(newInvalidRows);
  };

  const isRunButtonEnabled = inputRows.length > 0;

  return (
    <div className="DRH">
      <div className="DRHL">
        {/* Queue Section - unchanged from your original */}
        <div className="queue-section-title">
          <div>
            <div className="queue1">
              Queue
              <div
                className={`run-btn ${!isRunButtonEnabled ? 'disabled' : ''}`}
                onClick={isRunButtonEnabled ? handleRunQueue : undefined}
              >
                Run <FontAwesomeIcon icon={faChevronCircleRight} className="iconrun" />
              </div>
            </div>
          </div>
          <div className="queue-actions">
            <div className="addjr" onClick={addNewInputRow}>
              <div className="addjr1"><FontAwesomeIcon icon={faLayerGroup} /></div>
              <button className="add-job-btn">Add Report</button>
            </div>
          </div>
        </div>
        <div className="queue-section">
          {inputRows.length > 0 ? (
            inputRows.map(report => (
              <TempInputRow
                key={report.id} report={report} setNewReport={setNewReport}
                handleDeleteReport={handleDeleteReport} isInvalid={invalidRows.has(report.id)}
                flash={flashInvalidRows && invalidRows.has(report.id)}
              />
            ))
          ) : (
            <div className="no-reports-placeholder">No pending reports</div>
          )}
        </div>
  
        {/* Recents Section - with original structure preserved as much as possible */}
        <div className="recents-section-title">Files</div>
        <div className="DRHLTOP"> {/* Your original structure for search/filter */}
          <div className="DRHLTOPBOX">
            <div className="DRHLTOPBOXL"><input type="text" placeholder="Search" /></div>
            <div className="DRHLTOPBOXR">▼ Filter</div>
          </div>
        </div>
        <div className="recents-section"> {/* Items list and loading indicators go here */}
          {isLoadingInitial && <p className="loading-indicator">Loading reports...</p>}
          {!isLoadingInitial && items.length === 0 && (
            <p className="no-reports-indicator">No reports found.</p>
          )}
          
          {/* Render RecentReports only if not initial loading AND there are items */}
          {!isLoadingInitial && items.length > 0 && (
            <RecentReports items={items} />
          )}
          
          {/* Loading indicator for "load more" / infinite scroll */}
          {isLoadingMore && <p className="loading-indicator">Loading more reports...</p>}
          
          {/* Invisible target for IntersectionObserver */}
          {/* Only render this if there's a next page to prevent observer firing unnecessarily */}
          {hasNextPage && !isLoadingInitial && (
             <div ref={observerTarget} style={{ height: '1px', margin: '1px' }} />
          )}
        </div>
      </div>
      <div className="DRHR">
       {/* <StorageInfo /> */} {/* Or "Storage Info Coming Soon" */}
      </div>
    </div>
  );
}

export default ReportGen;